import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { Download, Users, Activity, Share2, Clock, Star } from 'lucide-react';
import api from '@/lib/api';
import { formatTL, monthLabel, TIER_COLORS } from '@/lib/format';
import { useTokens } from '@/lib/theme';
import { Card, Empty } from '@/components/shared';

export default function AdminReports() {
  const [data, setData] = useState(null);
  const t = useTokens();
  const grid = t.ink + '20';
  const axis = t.ink + '99';

  useEffect(() => {
    api.get('/admin/reports').then((r) => setData(r.data)).catch(() => {});
  }, []);

  const exportCsv = async (type, filename) => {
    try {
      const r = await api.get(`/admin/reports/export?type=${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { /* noop */ }
  };

  if (!data) return <div className="text-navy/40 text-sm">Yükleniyor...</div>;

  const refMap = {};
  (data.referralByMonth || []).forEach((r) => { refMap[r.month] = r.referral; });
  const monthData = data.byMonth.map((m) => ({ ...m, label: monthLabel(m.month), volumeK: Math.round(m.volume / 1000), referral: Math.round(refMap[m.month] || 0) }));
  const storeData = data.byStore.map((s) => ({ ...s, commissionK: Math.round(s.commission) }));

  const kpis = [
    { icon: Users, label: 'Aktif mimar (≥1 satış)', value: `${data.activeArchitects}/${data.totalArchitects}`, testid: 'kpi-active' },
    { icon: Activity, label: 'Aktivasyon oranı', value: `%${Math.round((data.activationRate || 0) * 100)}`, testid: 'kpi-activation' },
    { icon: Share2, label: 'K-faktör (davet katsayısı)', value: (data.kFactor ?? 0).toFixed(2), testid: 'kpi-kfactor' },
    { icon: Clock, label: 'Ort. ilk satış süresi', value: data.avgTimeToFirstSaleDays != null ? `${data.avgTimeToFirstSaleDays} gün` : '-', testid: 'kpi-ttfs' },
  ];

  return (
    <div className="fade-up" data-testid="admin-reports">
      <h1 className="font-serif text-3xl text-navy mb-1">Raporlar</h1>
      <p className="text-navy/50 text-sm mb-6">Mağaza, mimar, dönem ve seviye bazlı analiz</p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="reports-kpis">
        {kpis.map((k) => (
          <Card key={k.testid} className="p-5" data-testid={k.testid}>
            <k.icon strokeWidth={1.25} className="w-5 h-5 text-gold mb-2" />
            <p className="font-serif text-2xl text-navy">{k.value}</p>
            <p className="text-xs text-navy/50 mt-0.5">{k.label}</p>
          </Card>
        ))}
      </div>

      {/* Commission vs referral vs clubcard summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="reports-summary">
        <Card className="p-5">
          <p className="text-navy/50 text-sm">Toplam maliyet</p>
          <p className="font-serif text-xl md:text-2xl text-navy mt-1" data-testid="reports-total-cost">{formatTL(data.totalCommissionCost || 0)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-navy/50 text-sm">Satış komisyonu</p>
          <p className="font-serif text-xl md:text-2xl text-greenx mt-1">{formatTL(data.commissionTotal || 0)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-navy/50 text-sm">Davet primi</p>
          <p className="font-serif text-xl md:text-2xl text-gold mt-1" data-testid="reports-referral-total">{formatTL(data.referralTotal || 0)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-navy/50 text-sm">Kulüp kartı bonusu</p>
          <p className="font-serif text-xl md:text-2xl text-gold mt-1" data-testid="reports-clubcard-total">{formatTL(data.clubCardTotal || 0)}</p>
        </Card>
      </div>


      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <h2 className="font-serif text-lg text-navy mb-4">Aylık ciro (bin ₺)</h2>
          {monthData.length === 0 ? <Empty text="Veri yok" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: axis }} />
                <YAxis tick={{ fontSize: 11, fill: axis }} />
                <Tooltip formatter={(v) => `${v}K ₺`} />
                <Line type="monotone" dataKey="volumeK" stroke={t.gold} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="font-serif text-lg text-navy mb-4">Mağaza bazlı komisyon (₺)</h2>
          {storeData.length === 0 ? <Empty text="Veri yok" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={storeData}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="store" tick={{ fontSize: 9, fill: axis }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11, fill: axis }} />
                <Tooltip formatter={(v) => formatTL(v)} />
                <Bar dataKey="commissionK" fill={t.pos} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5 lg:col-span-2">
          <h2 className="font-serif text-lg text-navy mb-4">Aylık referans bonusu (₺)</h2>
          {monthData.length === 0 ? <Empty text="Veri yok" /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: axis }} />
                <YAxis tick={{ fontSize: 11, fill: axis }} />
                <Tooltip formatter={(v) => formatTL(v)} />
                <Bar dataKey="referral" fill={t.gold} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5 lg:col-span-2">
          <h2 className="font-serif text-lg text-navy mb-4">Seviye dağılımı</h2>
          <div className="flex flex-wrap gap-4" data-testid="tier-distribution">
            {data.byTier.map((t) => (
              <div key={t.tier} className="flex items-center gap-3 bg-cream border border-navy/10 rounded-lg px-5 py-3">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: TIER_COLORS[t.tier] }} />
                <div>
                  <p className="font-serif text-2xl text-navy">{t.count}</p>
                  <p className="text-xs text-navy/50">{t.tier}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg text-navy">Mağaza detayları</h2>
            <button onClick={() => exportCsv('stores', 'rapor-magazalar.csv')} data-testid="export-stores"
              className="inline-flex items-center gap-1.5 text-xs border border-navy/20 text-navy px-3 py-1.5 rounded-md active:scale-95 transition-transform">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-navy/50 text-xs border-b border-navy/10">
                  <th className="py-2 pr-4 font-medium">Mağaza</th>
                  <th className="py-2 pr-4 font-medium">Satış adedi</th>
                  <th className="py-2 pr-4 font-medium">Ciro</th>
                  <th className="py-2 pr-4 font-medium">Komisyon</th>
                </tr>
              </thead>
              <tbody>
                {data.byStore.map((s) => (
                  <tr key={s.storeId} className="border-b border-navy/10">
                    <td className="py-2 pr-4 text-navy font-medium">{s.store}</td>
                    <td className="py-2 pr-4 text-navy/70">{s.count}</td>
                    <td className="py-2 pr-4 text-navy/70">{formatTL(s.volume)}</td>
                    <td className="py-2 pr-4 text-greenx">{formatTL(s.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg text-navy">Mimar bazlı performans</h2>
            <button onClick={() => exportCsv('architects', 'rapor-mimarlar.csv')} data-testid="export-architects"
              className="inline-flex items-center gap-1.5 text-xs border border-navy/20 text-navy px-3 py-1.5 rounded-md active:scale-95 transition-transform">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-navy/50 text-xs border-b border-navy/10">
                  <th className="py-2 pr-4 font-medium">Mimar</th>
                  <th className="py-2 pr-4 font-medium">Şehir</th>
                  <th className="py-2 pr-4 font-medium">Seviye</th>
                  <th className="py-2 pr-4 font-medium">Satış</th>
                  <th className="py-2 pr-4 font-medium">Ciro</th>
                  <th className="py-2 pr-4 font-medium">Komisyon</th>
                </tr>
              </thead>
              <tbody data-testid="architect-report-list">
                {(data.byArchitect || []).slice(0, 20).map((a) => (
                  <tr key={a.architectId} className="border-b border-navy/10">
                    <td className="py-2 pr-4 text-navy font-medium">{a.name}{a.vip && <Star className="inline w-3 h-3 text-gold ml-1 -mt-0.5" fill="currentColor" strokeWidth={1.5} />}</td>
                    <td className="py-2 pr-4 text-navy/60">{a.city}</td>
                    <td className="py-2 pr-4"><span style={{ color: TIER_COLORS[a.tier] }}>{a.tier}</span></td>
                    <td className="py-2 pr-4 text-navy/70">{a.count}</td>
                    <td className="py-2 pr-4 text-navy/70">{formatTL(a.volume)}</td>
                    <td className="py-2 pr-4 text-greenx">{formatTL(a.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
